import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Box, Typography } from '@mui/material';
import { Appointment, Employee } from '../../../../types';
import ErrorDialog from '../../../global/ErrorDialog';
import ConfirmationDialog from '../../../global/ConfirmationDialog';
import LoadingSpinner from '../../../global/LoadingSpinner';
import { fetchEmployees, fetchEmployeeScheduleDay } from '../../../../firebase/firebase-delegate';
import { formatDate, formatTimeRange, isTimeFree, timeToMinutes } from '../../../../time/timeUtils';

interface AppointmentHandoffDialogProps {
  open: boolean;
  appointment: Appointment;
  employees?: Employee[];
  onClose: () => void;
  onConfirm: (employee: Employee) => void;
  isSubmitting?: boolean;
}

const AppointmentHandoffDialog: React.FC<AppointmentHandoffDialogProps> = ({
  open,
  appointment,
  employees: employeesProp,
  onClose,
  onConfirm,
  isSubmitting = false,
}) => {
  const [employees, setEmployees] = useState<Employee[]>(employeesProp ?? []);
  const [availability, setAvailability] = useState<Record<string, boolean>>({});
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canCheckAvailability = useMemo(() => {
    return Boolean(appointment.appointmentDate && appointment.appointmentTime && appointment.appointmentDuration);
  }, [appointment.appointmentDate, appointment.appointmentTime, appointment.appointmentDuration]);

  useEffect(() => {
    if (!open) {
      setConfirmDialogOpen(false);
      return;
    }

    setSelectedEmployeeId(appointment.employeeID);
  }, [open, appointment.employeeID]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let isCancelled = false;
    const loadAvailability = async () => {
      setIsLoading(true);
      try {
        const employeeList = employeesProp ?? (await fetchEmployees());
        if (isCancelled) {
          return;
        }
        setEmployees(employeeList);

        if (!canCheckAvailability) {
          setAvailability({});
          return;
        }

        const availabilityEntries = await Promise.all(
          employeeList.map(async (employee) => {
            if (employee.id === appointment.employeeID) {
              return [employee.id, true] as const;
            }

            try {
              const bitString = await fetchEmployeeScheduleDay(employee.id, appointment.appointmentDate);
              if (!bitString) {
                return [employee.id, false] as const;
              }
              const isFree = isTimeFree(
                bitString,
                timeToMinutes(appointment.appointmentTime),
                appointment.appointmentDuration
              );
              return [employee.id, isFree] as const;
            } catch (availabilityError) {
              console.error('Failed to check availability for employee', employee.id, availabilityError);
              return [employee.id, false] as const;
            }
          })
        );

        if (!isCancelled) {
          setAvailability(Object.fromEntries(availabilityEntries));
        }
      } catch (loadError) {
        if (!isCancelled) {
          const message = loadError instanceof Error ? loadError.message : 'Failed to load employees.';
          setError(message);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadAvailability();

    return () => {
      isCancelled = true;
    };
  }, [
    open,
    employeesProp,
    canCheckAvailability,
    appointment.employeeID,
    appointment.appointmentDate,
    appointment.appointmentTime,
    appointment.appointmentDuration,
  ]);

  const handleRequestConfirm = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirm = () => {
    if (isSubmitting) {
      return;
    }
    const selected = employees.find((employee) => employee.id === selectedEmployeeId);
    if (!selected) {
      return;
    }
    setConfirmDialogOpen(false);
    onConfirm(selected);
  };

  const selectedIsCurrent = selectedEmployeeId === appointment.employeeID;
  const selectedIsAvailable = selectedEmployeeId ? availability[selectedEmployeeId] : false;
  const confirmDisabled = !selectedEmployeeId || selectedIsCurrent || !selectedIsAvailable || isSubmitting;
  const selectedEmployee = selectedEmployeeId
    ? employees.find((employee) => employee.id === selectedEmployeeId) ?? null
    : null;

  const readableDate = appointment.appointmentDate ? formatDate(appointment.appointmentDate) : 'Unknown date';
  const readableTime = appointment.appointmentTime
    ? formatTimeRange(appointment.appointmentTime, appointment.appointmentDuration)
    : 'Unknown time';

  return (
    <>
      <Dialog open={open} onClose={isSubmitting ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>Hand Off Appointment</DialogTitle>
        <DialogContent sx={{ minHeight: 280 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Date
            </Typography>
            <Typography variant="body1">{readableDate}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Time
            </Typography>
            <Typography variant="body1">{readableTime}</Typography>
          </Box>

          {!canCheckAvailability && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              Appointment date or time is missing. Please reschedule first.
            </Typography>
          )}

          {isLoading ? (
            <Box sx={{ minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LoadingSpinner />
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={1}>
              {employees.map((employee) => {
                const isAvailable = availability[employee.id];
                const isSelected = selectedEmployeeId === employee.id;
                const isCurrent = appointment.employeeID === employee.id;

                return (
                  <Button
                    key={employee.id}
                    variant="outlined"
                    onClick={() => setSelectedEmployeeId(employee.id)}
                    disabled={!isAvailable}
                    sx={{
                      justifyContent: 'space-between',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      color: isAvailable ? 'text.primary' : 'text.disabled',
                      backgroundColor: isSelected ? 'action.hover' : 'transparent',
                    }}
                  >
                    <Box textAlign="left">
                      <Typography variant="body1">
                        {employee.firstName} {employee.lastName}
                      </Typography>
                      <Typography variant="caption" color={isAvailable ? 'text.secondary' : 'text.disabled'}>
                        {isAvailable ? 'Available' : 'Unavailable'}
                      </Typography>
                    </Box>
                    {isCurrent && (
                      <Typography variant="caption" color="primary">
                        Current
                      </Typography>
                    )}
                  </Button>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleRequestConfirm} color="primary" disabled={confirmDisabled}>
            {isSubmitting ? 'Handing Off...' : 'Confirm Handoff'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        title="Confirm Staff Handoff"
        message={
          selectedEmployee
            ? `Hand off this appointment to ${selectedEmployee.firstName} ${selectedEmployee.lastName}?`
            : 'Hand off this appointment to the selected staff member?'
        }
        onConfirm={handleConfirm}
        confirmButtonLabel={isSubmitting ? 'Handing Off...' : 'Yes, hand off'}
        cancelButtonLabel="Back"
      />

      {error && (
        <ErrorDialog
          open={Boolean(error)}
          onClose={() => setError(null)}
          message={error}
        />
      )}
    </>
  );
};

export default AppointmentHandoffDialog;
